import React from 'react';
import { CzechSuklRsAtcGroupResponse } from 'com.cogvio.pricemonitor.api';
import { Checkbox } from '~/libs/ui/atoms/Checkbox';
import { TailwindTooltip } from '~/libs/ui/atoms/TailwindTooltip';

import { getAvailabilityTooltip } from '../utils/availabilityTooltip';
import { iterateGroupProducts } from '../utils/iterateGroupProducts';
import { resolveAvailabilityMetric } from '../utils/resolveAvailabilityMetric';
import { formatIdenticalLpCount } from '../utils/formatIdenticalLpCount';
import { formatProductDncDou } from '../utils/formatVersionDncDou';
import { AvailabilityShareBar } from './AvailabilityShareBar';
import { RowAvailabilityIcon } from './RowAvailabilityIcon';

export interface RowCardProps {
	group: CzechSuklRsAtcGroupResponse;
	dddShareThreshold: number;
	className?: string;
	selectable?: boolean;
	selected?: boolean;
	selectionDisabled?: boolean;
	/** Hides the checkbox control while keeping selection working via clicking the card. */
	hideCheckbox?: boolean;
	onToggleSelect?(group: CzechSuklRsAtcGroupResponse, selected: boolean): void;
}

const fieldLabel = (title: string) => (
	<span className='block mb-1 text-labelsmall text-gray-500 leading-tight'>{title}</span>
);

export function RowCard({
	group,
	dddShareThreshold,
	className = '',
	selectable,
	selected,
	selectionDisabled,
	hideCheckbox,
	onToggleSelect,
}: RowCardProps) {
	const products = iterateGroupProducts(group);
	const [atcCode, ...atcRest] = group.atc.split(' ');
	const activeSubstance = atcRest.join(' ');

	const canClickToSelect = selectable && hideCheckbox && !(selectionDisabled && !selected);
	const isCard = Boolean(selectable && hideCheckbox);

	return (
		<div
			className={`rounded-lg border transition-colors font-display ${
				isCard
					? `${canClickToSelect ? 'cursor-pointer' : ''} ${
							selected ? 'border-cBlue-500 bg-cBlue-50' : 'border-gray-200 hover:border-gray-300'
					  }`
					: `border-gray-200 ${selectable && selected ? 'border-cBlue-500 bg-cBlue-50' : 'bg-white'}`
			} ${className}`}
			onClick={canClickToSelect ? () => onToggleSelect?.(group, !selected) : undefined}
		>
			<div className='flex items-start gap-3 px-3 py-3 bg-gray-50 rounded-t-lg border-b border-gray-200'>
				{selectable && !hideCheckbox && (
					<div className='pt-0.5'>
						<Checkbox
							id={`availability-card-select-${group.id}`}
							checked={Boolean(selected)}
							disabled={selectionDisabled && !selected}
							onChange={(checked) => onToggleSelect?.(group, checked)}
						/>
					</div>
				)}
				<div className='w-20 flex-shrink-0'>
					{fieldLabel('RS')}
					<h2 className='text-md font-semibold text-gray-900 leading-5 uppercase tabular-nums'>{group.rs ?? '–'}</h2>
				</div>
				<div className='flex-1 min-w-0'>
					{fieldLabel('ATC')}
					<h2 className='text-base font-bold text-gray-900 leading-5 uppercase break-words'>
						<span className='text-cBlue-500 mr-2 tabular-nums'>{atcCode}</span>
						{activeSubstance}
					</h2>
				</div>
			</div>

			<div className='divide-y divide-gray-200'>
				{products.map(({ product, productIndex, versions }) => {
					const metric = resolveAvailabilityMetric(product.availability);
					const mainBrand = versions[0]?.brand ?? '–';
					const isAvailable = product.availability.status === 'available';
					const isMultiVersion = versions.length > 1;
					const dncDouDisplay = formatProductDncDou(versions);

					return (
						<div key={`product-${productIndex}`} className='px-3 py-3 space-y-3'>
							<div className='flex items-start justify-between gap-3'>
								<div className='min-w-0'>
									{fieldLabel('NÁZEV LP')}
									<h3 className='text-base font-bold text-gray-900 leading-5 uppercase break-words'>{mainBrand}</h3>
									{isMultiVersion ? (
										<span className='inline-block mt-1 text-xs font-semibold text-cBlue-600 uppercase tracking-wide'>
											{formatIdenticalLpCount(versions.length)}
										</span>
									) : null}
								</div>
								<TailwindTooltip
									reference={
										<div className='flex items-center gap-1.5 pt-0.5 flex-shrink-0'>
											<RowAvailabilityIcon status={product.availability.status} />
											<span className='text-sm font-semibold text-gray-700 whitespace-nowrap'>
												{isAvailable ? 'Dostupný' : 'Nedostupný'}
											</span>
										</div>
									}
									displayClass='inline-block'
									position='left'
								>
									{getAvailabilityTooltip({
										status: product.availability.status,
										isOverThreshold: product.availability.decisionDetails.isOverThreshold,
										isFullyReimbursed: product.availability.decisionDetails.isFullyReimbursed,
										hasPriceAgreement: product.availability.decisionDetails.hasPriceAgreement,
										thresholdPercent: dddShareThreshold,
									})}
								</TailwindTooltip>
							</div>

							<div>
								{fieldLabel('DOSTUPNOST')}
								<AvailabilityShareBar
									label={metric.label}
									value={metric.value}
									share={metric.share}
									shareThreshold={dddShareThreshold}
								/>
							</div>

							<div>
								{fieldLabel('DNC/DoÚ')}
								<span className='text-sm text-gray-900 leading-5 uppercase'>{dncDouDisplay}</span>
							</div>

							<div
								className={
									isMultiVersion
										? 'border border-dashed border-gray-300 divide-y divide-dashed divide-gray-300 p-3 space-y-0'
										: 'space-y-3'
								}
							>
								{versions.map((version, versionIndex) => {
									const versionBrand = version.brand ?? '';
									const showSecondaryBrand = versionIndex > 0 && versionBrand && versionBrand !== mainBrand;
									const isSubsequentVersion = versionIndex > 0;

									return (
										<div
											key={version.suklCode ?? `${productIndex}-${versionIndex}`}
											className={
												isMultiVersion
													? `py-3 first:pt-0 last:pb-0${isSubsequentVersion ? ' opacity-60' : ''}`
													: undefined
											}
										>
											{isSubsequentVersion ? (
												<span className='block mb-1 text-xs text-gray-400 uppercase'>Totožný LP</span>
											) : null}
											{showSecondaryBrand ? (
												<div className='mb-2'>
													{fieldLabel('NÁZEV LP')}
													<p className='text-base font-bold text-gray-900 leading-5 uppercase break-words'>
														{versionBrand}
													</p>
												</div>
											) : null}
											{version.description ? (
												<div className='mb-2'>
													{fieldLabel('DOPLNĚK NÁZVU')}
													<p
														className={`text-md leading-5 uppercase tabular-nums break-words ${
															isMultiVersion && !isSubsequentVersion
																? 'font-bold text-gray-900'
																: 'font-semibold text-gray-900'
														}`}
													>
														{version.description}
													</p>
												</div>
											) : null}
											<div>
												{fieldLabel('SÚKL KÓD')}
												<span
													className={`text-label leading-5 uppercase tabular-nums ${
														isMultiVersion && !isSubsequentVersion
															? 'font-bold text-gray-900'
															: 'font-semibold text-gray-900'
													}`}
												>
													{version.suklCode ?? '–'}
												</span>
											</div>
										</div>
									);
								})}
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
}
